import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Welle 12 / pre203: Document-Picture-in-Picture für den Companion.
 *
 * Ab pre203 wird NICHT mehr ein separates Mini-Panel imperativ gebaut, sondern das
 * abgedockte Fenster zeigt das **komplette Companion-Fenster**. Dazu wird der React-
 * Root-Container (#root) beim Abdocken in das PiP-Fenster verschoben. Weil React seine
 * Event-Listener am Root-Container hängen hat, wandern sie mit — die UI bleibt voll
 * interaktiv, ohne die bekannten Portal-über-Fenstergrenzen-Probleme. Beim Zurückholen/
 * Schließen wandert #root zurück ins Hauptfenster.
 *
 * Chromium/Edge ab 116; sonst supported=false.
 */

// Typen bleiben exportiert (Rückwärtskompatibilität für Importe), werden aber nicht
// mehr für den PiP-Inhalt gebraucht.
export interface CompanionPipState {
  callId?: string | null;
  incomingName?: string | null;
  inCall?: boolean;
  micMuted?: boolean;
  camOn?: boolean;
  guestConnected?: boolean;
  recording?: boolean;
  recPending?: boolean;
  recTopicLabel?: string;
  canRecord?: boolean;
}
export interface CompanionPipHandlers {
  accept: () => void;
  decline: () => void;
  hangup: () => void;
  toggleMic: () => void;
  toggleCam: () => void;
  kickGuest: () => void;
  transfer: () => void;
  startRecording: () => void;
  stopRecording: () => void;
}

function copyStyles(pip: Window) {
  // Alle Stylesheets (Vite-<style> im Dev, gebündelte <link> im Build) + Fonts kopieren.
  for (const node of Array.from(document.head.querySelectorAll('style, link[rel="stylesheet"]'))) {
    try { pip.document.head.appendChild(node.cloneNode(true)); } catch { /* */ }
  }
  try {
    // Konstruierte Stylesheets (falls vorhanden) übernehmen.
    const adopted = (document as any).adoptedStyleSheets as CSSStyleSheet[] | undefined;
    if (adopted && adopted.length) (pip.document as any).adoptedStyleSheets = [...adopted];
  } catch { /* */ }
  // Overflow im kleinen PiP-Fenster erlauben (global.css setzt body{overflow:hidden}).
  const s = pip.document.createElement('style');
  s.textContent = 'html,body{height:100%;margin:0;padding:0}body{overflow:auto !important}';
  pip.document.head.appendChild(s);
}

export function useCompanionPip() {
  const supported = typeof window !== 'undefined' && 'documentPictureInPicture' in window;
  const [active, setActive] = useState(false);
  const winRef = useRef<Window | null>(null);
  const placeholderRef = useRef<HTMLElement | null>(null);

  // #root zurück ins Hauptfenster holen + Platzhalter entfernen. Idempotent.
  const restore = useCallback(() => {
    const root = document.getElementById('root');
    if (root && root.ownerDocument !== document) {
      try { document.body.appendChild(root); } catch { /* */ }
    }
    if (placeholderRef.current) { try { placeholderRef.current.remove(); } catch { /* */ } placeholderRef.current = null; }
    winRef.current = null;
    setActive(false);
  }, []);

  const close = useCallback(() => {
    try { winRef.current?.close(); } catch { /* */ }
    restore();
  }, [restore]);

  const open = useCallback(async () => {
    if (!supported || winRef.current) return;
    const root = document.getElementById('root');
    if (!root) return;
    try {
      const pip: Window = await (window as any).documentPictureInPicture.requestWindow({ width: 480, height: 780 });
      winRef.current = pip;

      copyStyles(pip);
      const theme = document.documentElement.getAttribute('data-theme');
      if (theme) pip.document.documentElement.setAttribute('data-theme', theme);
      pip.document.documentElement.setAttribute('lang', document.documentElement.getAttribute('lang') || 'de');

      // Platzhalter im Hauptfenster, solange abgedockt.
      const ph = document.createElement('div');
      ph.style.cssText = 'position:fixed;inset:0;z-index:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;background:var(--bg,#0a0a0a);color:var(--fg,#e8e8e8);font-family:Rajdhani,system-ui,sans-serif;text-align:center;padding:24px';
      const title = document.createElement('div');
      title.style.cssText = 'font-size:16px;font-weight:600';
      title.textContent = 'Companion ist abgedockt';
      const sub = document.createElement('div');
      sub.style.cssText = 'font-size:13px;opacity:.7;max-width:320px;line-height:1.5';
      sub.textContent = 'Die Steuerung läuft im schwebenden Fenster im Vordergrund.';
      const btn = document.createElement('button');
      btn.textContent = 'Zurückholen';
      btn.style.cssText = 'font-family:inherit;font-size:13px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;padding:9px 18px;border-radius:8px;border:1px solid var(--hairline,#333);background:var(--bg-elev-2,#1c1c1c);color:var(--fg,#e8e8e8);cursor:pointer';
      btn.onclick = () => { try { pip.close(); } catch { /* */ } restore(); };
      ph.append(title, sub, btn);
      document.body.appendChild(ph);
      placeholderRef.current = ph;

      // #root ins PiP-Fenster verschieben — React rendert weiter hinein, Events bleiben aktiv.
      pip.document.body.appendChild(root);

      pip.addEventListener('pagehide', () => restore(), { once: true });
      setActive(true);
    } catch (e) {
      console.warn('[companion-pip] requestWindow fehlgeschlagen', e);
      restore();
    }
  }, [supported, restore]);

  // Beim Unmount (Call-Ende/View-Wechsel) PiP schließen und Root zurückholen.
  useEffect(() => () => { try { winRef.current?.close(); } catch { /* */ } }, []);

  return { supported, active, open, close };
}
